<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{ content: collapsed ? 'w-64' : 'w-(--reka-dropdown-menu-trigger-width) min-w-64' }"
  >
    <UTooltip :text="displayName" :disabled="!collapsed">
      <UButton
        color="neutral"
        variant="ghost"
        block
        :square="collapsed"
        :aria-label="$t('user.menuLabel')"
        class="data-[state=open]:bg-elevated"
      >
        <UAvatar :alt="displayName" size="sm" />
        <span v-if="!collapsed" class="flex min-w-0 flex-1 flex-col text-left leading-tight">
          <span class="truncate text-sm font-medium text-highlighted">{{ displayName }}</span>
          <span v-if="primaryRoleLabel" class="truncate text-xs text-muted">{{ primaryRoleLabel }}</span>
        </span>
        <UIcon v-if="!collapsed" name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
      </UButton>
    </UTooltip>

    <template #identity>
      <div class="flex w-full flex-col gap-3 py-1 font-normal">
        <div class="flex items-center gap-3">
          <UAvatar :alt="displayName" size="md" />
          <div class="flex min-w-0 flex-col">
            <span class="truncate text-sm font-semibold text-highlighted">{{ displayName }}</span>
            <span v-if="organizationName" class="flex items-center gap-1 truncate text-xs text-muted">
              <UIcon name="i-lucide-building-2" class="size-3.5 shrink-0" />
              <span class="truncate">{{ organizationName }}</span>
            </span>
          </div>
        </div>

        <div v-if="roleLabels.length > 0" class="flex flex-wrap gap-1">
          <UBadge
            v-for="role in roleLabels"
            :key="role"
            :label="role"
            color="primary"
            variant="subtle"
            size="sm"
            icon="i-lucide-user-round-check"
          />
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-muted">{{ $t('user.worksCouncils') }}</span>
          <div v-if="worksCouncilLabels.length > 0" class="flex flex-wrap gap-1">
            <UBadge
              v-for="council in worksCouncilLabels"
              :key="council"
              :label="council"
              color="neutral"
              variant="subtle"
              size="sm"
              icon="i-lucide-users"
            />
          </div>
          <span v-else class="text-xs text-muted">{{ $t('user.noWorksCouncil') }}</span>
        </div>
      </div>
    </template>
  </UDropdownMenu>
</template>

<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

defineProps<{
  collapsed?: boolean
}>()

const { t: $t } = useI18n()
const { displayName, primaryRoleLabel, roleLabels, organizationName, worksCouncilLabels } = useUserIdentity()
const runtimeConfig = useRuntimeConfig()

const items = computed<DropdownMenuItem[][]>(() => [
  [
    {
      type: 'label',
      label: displayName.value,
      slot: 'identity'
    }
  ],
  [
    {
      label: $t('user.administration'),
      icon: 'i-lucide-shield',
      to: '/administration'
    },
    {
      label: $t('user.settings'),
      icon: 'i-lucide-settings',
      to: '/settings'
    },
    {
      label: `${$t('changelog.pageTitle')} (${runtimeConfig.public.appVersion})`,
      icon: 'i-lucide-book-open',
      to: '/changelog'
    }
  ],
  [
    {
      label: $t('user.logout'),
      icon: 'i-lucide-log-out',
      async onSelect(e: Event) {
        e.preventDefault()
        await navigateTo('/auth/logout', { external: true })
      }
    }
  ]
])
</script>
